import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { acquireSharedRealtimeChannel } from "@/lib/sharedRealtimeChannel";
import {
  settingsSquadsQueryKey,
  squadAdminKey,
  userSquadsQueryKey,
} from "@/lib/squad";

type SquadMemberRoleRow = {
  squad_id?: string;
  user_id?: string;
  role?: string;
  status?: string;
};

/**
 * Keeps the current user's admin UI in sync when another admin changes their
 * role on `squad_members`. UPDATE filters work (unlike DELETE); the gap was
 * that nothing invalidated `useIsSquadAdmin` / settings role caches.
 */
export function useSquadRoleSync() {
  const { user, isGuest, hydrated } = useAuth();
  const queryClient = useQueryClient();
  const userId = user && !isGuest ? user.id : undefined;

  useEffect(() => {
    if (!hydrated || !userId) return;

    const invalidateRoleCaches = async (squadId: string) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: squadAdminKey(squadId, userId) }),
        queryClient.invalidateQueries({ queryKey: settingsSquadsQueryKey(userId) }),
        queryClient.invalidateQueries({ queryKey: userSquadsQueryKey(userId) }),
        queryClient.invalidateQueries({ queryKey: ["squad-page"] }),
        queryClient.invalidateQueries({ queryKey: ["squad-members-settings", squadId] }),
      ]);
    };

    const topic = `squad-role-sync-${userId}`;
    return acquireSharedRealtimeChannel(
      topic,
      (broadcast) =>
        supabase
          .channel(topic)
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "squad_members",
              filter: `user_id=eq.${userId}`,
            },
            (payload) => {
              const next = payload.new as SquadMemberRoleRow;
              const prev = payload.old as SquadMemberRoleRow;
              const squadId = next.squad_id;
              if (!squadId) return;
              if (next.status !== "active") return;
              // Skip no-op updates (e.g. unrelated column) when old.role is present.
              if (prev.role != null && prev.role === next.role) return;

              void invalidateRoleCaches(squadId);
              broadcast();
            },
          )
          .subscribe(),
      () => {
        /* Primary work runs in channel handlers. */
      },
    );
  }, [hydrated, userId, queryClient]);
}
